import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Resource } from '@/lib/api/types'

/** The referral program, run by Arva SEO. Amounts are cents (USD). */
export interface ReferralDashboard {
  enabled: boolean
  commission_percent: number
  can_pay: boolean
  totals: {
    partners: number
    signups: number
    signups_30_days: number
    customers: number
    /** Percent of referred sign-ups that bought a plan; null before any sign-up. */
    conversion: number | null
    /** Everything referred accounts have paid (after refunds, before tax). */
    revenue: number
    mrr: number
    first_payments: number
    commission_on_hold: number
    commission_earned: number
    commission_void: number
    /** What partners hold right now (owed). */
    balances: number
    paid_out: number
    payouts_waiting: number
    spent_on_plans: number
  }
  months: { month: string; signups: number; customers: number; revenue: number; commission: number }[]
  partners: {
    id: string
    name: string | null
    email: string | null
    code: string | null
    signups: number
    customers: number
    revenue: number
    earned: number
    on_hold: number
    balance: number
  }[]
  payouts: {
    id: string
    partner: { id: string; name: string; email: string } | null
    amount: number
    paypal_email: string
    status: 'requested' | 'paid' | 'cancelled'
    reference: string | null
    note: string | null
    handled_by: string | null
    requested_at: string | null
    paid_at: string | null
    cancelled_at: string | null
  }[]
  rewards: {
    id: string
    partner: { id: string; name: string } | null
    customer: { id: string; name: string } | null
    description: string
    sale_total: number
    percent: number
    commission: number
    status: 'on_hold' | 'credited' | 'void'
    hold_until: string
    void_reason: string | null
    created_at: string | null
  }[]
}

export function useReferralDashboard() {
  return useQuery({
    queryKey: ['referrals'],
    queryFn: async () => (await http.get<Resource<ReferralDashboard>>('/referrals')).data,
  })
}

/** Sent by PayPal (with its transaction ID), or cancelled with the reason; the partner is told. */
export function useHandlePayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; action: 'paid' | 'cancel'; reference?: string; note?: string }) =>
      (
        await http.post<Resource<ReferralDashboard>>(
          `/referral-payouts/${input.id}/${input.action}`,
          input.action === 'paid' ? { reference: input.reference || null } : { note: input.note },
        )
      ).data,
    onSuccess: (data) => queryClient.setQueryData(['referrals'], data),
  })
}
